import styled from "@emotion/styled";
import { notEmpty } from "@product/scmp-sdk";
import { UnsupportedBehavior } from "@shopify/react-idle";
import toNumber from "lodash/toNumber";
import type { FunctionComponent } from "react";

import { IdleQueue } from "~/components/common/idle-queue";
import type { ContentSchemaRenderProps } from "~/components/content/content-schema-render";
import { normalizeJsxAttribute } from "~/lib/utils";

import { IframeAllowValue, VideoSourceUrl } from "./enums";
import type { StyledProps } from "./styles";
import { Container, StyledIframe } from "./styles";

export type Props = {
  allow?: string;
  allowfullscreen?: string;
  frameborder?: string;
  height?: string;
  src?: string;
  width?: string;
} & ResponsiveAttributes &
  ContentSchemaRenderProps;

type ResponsiveAttributes = {
  ["data-mdheight"]?: string;
  ["data-responsive"]?: string;
  ["data-smheight"]?: string;
};

export const Component: FunctionComponent<Props> = ({
  allow,
  allowfullscreen,
  frameborder,
  height,
  reference: _content,
  schemaNode,
  src: source = "",
  width,
  ...attribs
}) => {
  const iframeAllowFullScreen = allowfullscreen ? true : false;
  const iframeAllow =
    iframeAllowFullScreen && !notEmpty(allow) ? IframeAllowValue.Fullscreen : allow;

  const computeVariant: () => StyledProps["$variant"] = () => {
    const isVideoSource = Object.values(VideoSourceUrl).some(url => source.includes(url));
    if (isVideoSource) return { $type: "video" };

    if (!width || !height) return { $aspectRatio: 16 / 9, $type: "aspectRatio" };

    const isResponsive = attribs["data-responsive"] === "1";
    if (isResponsive) {
      return {
        $responsiveBlockSize: {
          desktopUp: height,
          mobileUp: attribs["data-smheight"] ?? attribs["data-mdheight"] ?? height,
          tabletUp: attribs["data-mdheight"] ?? height,
        },
        $type: "responsive",
      };
    }

    const parsedWidth = toNumber(width);
    const parsedHeight = toNumber(height);
    if (parsedWidth > 0 && parsedHeight > 0)
      return { $aspectRatio: parsedWidth / parsedHeight, $type: "aspectRatio" };

    return { $blockSize: `${height}px`, $type: "static" };
  };

  return (
    <Container>
      <IdleQueue unsupportedBehavior={UnsupportedBehavior.AnimationFrame}>
        <StyledIframe
          $variant={computeVariant()}
          allow={iframeAllow}
          allowFullScreen={iframeAllowFullScreen}
          frameBorder={frameborder}
          src={source}
          {...normalizeJsxAttribute(schemaNode.attribs)}
          {...attribs}
        />
      </IdleQueue>
    </Container>
  );
};

Component.displayName = "GenericIframe";
export const GenericIframe = styled(Component)``;
